import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Mail, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { WhatsAppIcon } from "../components/icons/WhatsAppIcon";
import HoneypotField from "../components/HoneypotField";
import { http, formatApiErrorDetail } from "../lib/api";
import { getCurrentBrand } from "../hooks/useBranding";

export default function Contacto() {
  const [params] = useSearchParams();
  const intent = params.get("intent"); // null | "mejorar"
  const [form, setForm] = useState({
    nombre: "", email: "", telefono: "", comunidad_empresa: "",
    mensaje: intent === "mejorar" ? "Quiero que revisen mi póliza vigente." : "",
  });
  const [website, setWebsite] = useState(""); // honeypot
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const canSubmit = form.nombre && form.email && form.telefono && form.comunidad_empresa;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/contacto", { ...form, origen: intent || "web", website, partner: getCurrentBrand()?.key || null });
      setSent(true);
      toast.success("¡Mensaje enviado!", { description: "Te contactaremos en menos de 24 horas hábiles." });
    } catch (e) {
      toast.error("Error al enviar", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <Layout>
        <div className="container-x py-24 text-center max-w-xl mx-auto fade-in-up">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-7" style={{ background: "rgba(46,125,50,0.1)" }}>
            <CheckCircle2 size={40} color="#2E7D32" strokeWidth={1.8}/>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "Manrope" }}>
            Mensaje <span className="serif" style={{ color: "var(--brand-accent)" }}>recibido.</span>
          </h1>
          <p className="mb-8 text-lg" style={{ color: "var(--text-muted)" }}>
            Un especialista en seguros de comunidades te contactará en menos de <strong style={{ color: "var(--text-primary)" }}>24 horas hábiles</strong>.
          </p>
          <Link to="/" className="btn-primary">Volver al inicio <ArrowUpRight size={15}/></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="relative grain" style={{ background: "linear-gradient(180deg, var(--bg-cream) 0%, #FFFFFF 50%)" }}>
        <div aria-hidden className="absolute -top-32 -right-32 w-[440px] h-[440px] rounded-full opacity-[0.06] blur-3xl" style={{ background: "var(--brand-accent)" }}/>

        <div className="container-x relative py-12 md:py-24">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 lg:gap-20">
            {/* Left column */}
            <div className="md:col-span-5 fade-in-up">
              <div className="eyebrow mb-5">
                {intent === "mejorar" ? "Revisión gratuita" : "Hablemos"}
              </div>
              <h1 className="text-[40px] md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] md:leading-[1.0] mb-5 md:mb-6" style={{ fontFamily: "Manrope" }}>
                {intent === "mejorar" ? (
                  <>Envíanos tu<br/><span className="serif" style={{ color: "var(--brand-accent)" }}>póliza.</span></>
                ) : (
                  <>Cuéntanos de tu<br/><span className="serif" style={{ color: "var(--brand-accent)" }}>comunidad.</span></>
                )}
              </h1>
              <p className="text-base md:text-lg" style={{ color: "var(--text-muted)", lineHeight: 1.55 }}>
                {intent === "mejorar"
                  ? "La revisamos sin costo y te decimos honestamente si podemos mejorar precio o cobertura."
                  : "Un especialista te contactará en menos de 24 horas hábiles. Sin discurso de venta."}
              </p>

              <div className="mt-10 space-y-3">
                <ContactItem icon={Mail} label="Email" value="Lucas@micomunidadsegura.cl" href="mailto:Lucas@micomunidadsegura.cl"/>
                <ContactItem icon={WhatsAppIcon} label="WhatsApp" value="+56 9 4296 4199" href="https://wa.me/56942964199"/>
              </div>

              <div className="mt-10 flex items-start gap-3 p-5 rounded-xl border" style={{ borderColor: "var(--border-subtle)", background: "rgba(255,255,255,0.6)" }}>
                <Clock size={16} style={{ color: "var(--brand-accent)" }} className="mt-0.5 flex-shrink-0"/>
                <div className="text-[13px]" style={{ color: "var(--text-muted)", lineHeight: 1.5 }}>
                  <strong style={{ color: "var(--text-primary)" }}>Respuesta en 24 hrs hábiles.</strong> Sin compromiso. Sin spam.
                </div>
              </div>
            </div>

            {/* Right column: form */}
            <div className="md:col-span-7 fade-in-up stagger-2">
              <form onSubmit={submit} className="bg-white rounded-2xl p-6 md:p-10 border" style={{ borderColor: "var(--border-subtle)", boxShadow: "var(--shadow-card)" }} data-testid="contacto-form">
                <HoneypotField value={website} onChange={setWebsite} />
                <h2 className="text-2xl md:text-[28px] font-extrabold tracking-tight mb-1" style={{ fontFamily: "Manrope" }}>
                  {intent === "mejorar" ? "Pídenos la revisión" : "Déjanos tus datos"}
                </h2>
                <p className="text-sm mb-7" style={{ color: "var(--text-muted)" }}>
                  Toma menos de un minuto.
                </p>

                <div className="space-y-5">
                  <Fld label="Nombre">
                    <input className="field-input" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} placeholder="Tu nombre completo" required data-testid="input-nombre"/>
                  </Fld>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Fld label="Email">
                      <input type="email" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@email.com" required data-testid="input-email"/>
                    </Fld>
                    <Fld label="Teléfono">
                      <input type="tel" className="field-input" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+56 9 ..." required data-testid="input-telefono"/>
                    </Fld>
                  </div>
                  <Fld label="Nombre de la comunidad o empresa">
                    <input className="field-input" value={form.comunidad_empresa} onChange={(e) => set("comunidad_empresa", e.target.value)} placeholder="Edificio Las Higueras / Administradora X" required data-testid="input-comunidad"/>
                  </Fld>
                  <Fld label="Mensaje" hint="Opcional — adjunta detalles por email si lo necesitas">
                    <textarea rows={4} className="field-input" value={form.mensaje} onChange={(e) => set("mensaje", e.target.value)} placeholder={intent === "mejorar" ? "Ej. tenemos póliza con compañía X, vence en marzo..." : "Cuéntanos qué necesitas..."}/>
                  </Fld>
                </div>

                <button type="submit" className="btn-primary w-full justify-center mt-7" disabled={!canSubmit || busy} data-testid="submit-contacto">
                  {busy ? "Enviando..." : <>Enviar mensaje <ArrowUpRight size={15}/></>}
                </button>

                <div className="mt-6 flex items-center gap-2 text-[12px]" style={{ color: "var(--text-soft)" }}>
                  <ShieldCheck size={13}/>
                  <span>Datos protegidos bajo Ley 19.628. No compartimos información.</span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

const Fld = ({ label, hint, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
    {hint && <span className="field-hint">{hint}</span>}
  </div>
);

const ContactItem = ({ icon: Icon, label, value, href }) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    className="flex items-center gap-3.5 p-3 -ml-3 rounded-xl transition-all hover:bg-white group"
  >
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-[var(--brand-accent)] group-hover:scale-105" style={{ background: "rgba(10, 31, 54, 0.06)" }}>
      <Icon size={16} style={{ color: "var(--brand-navy)" }} className="group-hover:text-white transition-colors"/>
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-[15px] font-semibold transition-colors group-hover:text-[var(--brand-accent)]" style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  </a>
);
